import { useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import { Descendant } from 'slate'

import { IActionFormProps, VARIABLE_TEXT_FEATURE_FLAG_ALL, VariableTextFeatureFlag } from '@makyoapp/makyo-common'
import { Button, ButtonVariant, Field, Label, MaterialIconPicker } from '@makyoapp/makyo-ui'

import { convertLegacyValueToDescendant } from '@/lib/slate'
import { useForm } from '@/lib/use-form'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { getDataSourceKeyForComponent } from '@/shared/builder/utils/components'
import { BrandKitColorsDropdown } from '@/components/BrandKitColorsDropdown'
import { VariableTextV2 } from '@/components/VariableTextV2/VariableTextV2'

type ShowSnackbarSettings = {
  value: Descendant[]
  bgColor: string
  icon: string
  iconColor: string
  title: Descendant[]
}

export default function ActFormShowSnackbar({ onSubmit, onDismiss, initialData, triggerSource }: IActionFormProps) {
  const { activeLayerContainer } = useActiveLayerContext()
  const { activeComponent } = useActiveLayerContext()
  const dataSourceKey = getDataSourceKeyForComponent(activeComponent?.compId || '')
  const table = dataSourceKey
    ? (activeLayerContainer?.elementSettings?.[activeComponent?.id as string]?.[dataSourceKey] ?? null)
    : null
  const activeTableId = (table as any)?.table ?? table

  const [showIcon, setShowIcon] = useState(false)

  const { form, onSubmit: onFormSubmit } = useForm<ShowSnackbarSettings>({
    defaultValues: {
      ...initialData,
      value: [{ type: 'paragraph', children: [{ text: '' }] }],
      bgColor: initialData?.bgColor || 'primary-20',
      icon: initialData?.icon || '',
      iconColor: initialData?.iconColor || 'neutral-90',
      title: convertLegacyValueToDescendant(initialData?.title, 'Action Completed!'),
    },
  })

  const handleSubmit = onFormSubmit((data) => {
    onSubmit(data)
  })

  const contentValue = form.getValues('value')
  const titleValue = form.getValues('title')
  const isContentEmpty = useMemo(() => {
    const isTextOnly = (contentValue[0] as any).children.length === 0
    const firstParagaphText = (contentValue?.[0] as any)?.children[0]?.text
    return isTextOnly && !firstParagaphText?.trim().length
    //
  }, [contentValue])

  const invalidForm = isContentEmpty || !!form.errors

  let featureFlags =
    (VARIABLE_TEXT_FEATURE_FLAG_ALL &
      ~VariableTextFeatureFlag.Icon &
      ~VariableTextFeatureFlag.Image &
      ~VariableTextFeatureFlag.Column &
      ~VariableTextFeatureFlag.ColumnData) |
    VariableTextFeatureFlag.DateTime |
    VariableTextFeatureFlag.ActionOutput

  if (triggerSource === 'newsletter') {
    featureFlags = featureFlags | VariableTextFeatureFlag.NewsletterEmail
  }

  return (
    <div>
      <div className='p-4'>
        <Field>
          <Label required>Title</Label>
          <VariableTextV2
            tableId={activeTableId}
            featureFlags={
              initialData?.isActionWorkflow ? featureFlags : featureFlags & ~VariableTextFeatureFlag.ActionOutput
            }
            options={initialData?.actionOptions}
            placeholder='Enter title'
            value={titleValue as Descendant[]}
            onChange={(value) => {
              form.setValues('title', value)
            }}
          />
        </Field>

        <Field>
          <Label>Content</Label>

          <VariableTextV2
            tableId={activeTableId}
            featureFlags={
              initialData?.isActionWorkflow ? featureFlags : featureFlags & ~VariableTextFeatureFlag.ActionOutput
            }
            value={contentValue as Descendant[]}
            onChange={(value) => {
              form.setValues('value', value)
            }}
            options={initialData?.actionOptions}
            placeholder='Enter content message'
          />
        </Field>

        <Field>
          <Label>Background Color</Label>

          <BrandKitColorsDropdown
            value={form.getValues('bgColor')}
            onChange={(value) => form.setValues('bgColor', value)}
          />
        </Field>

        <Field>
          <Label>Icon</Label>
          <MaterialIconPicker
            show={showIcon}
            value={form.getValues('icon') || 'add'}
            onToggle={() => setShowIcon(true)}
            onClose={() => setShowIcon(false)}
            onClear={() => form.setValues('icon', '')}
            onDone={(icon) => form.setValues('icon', icon)}
            settings={{
              fill: 0,
              opsz: 24,
              wght: 300,
              grade: 0,
            }}
            clickAsDone
            className='w-10 min-h-10 h-fit rounded-md bg-zinc-200 dark:bg-slate-600 cursor-pointer'
            toggleClassName='p-2 w-10 min-h-10 cursor-pointer'
          />
        </Field>

        <Field>
          <Label>Icon Color</Label>
          <BrandKitColorsDropdown
            value={form.getValues('iconColor')}
            onChange={(value) => form.setValues('iconColor', value)}
          />
        </Field>
      </div>

      <div className='px-4 py-2 border-t bdr flex justify-between items-center'>
        <Button variant={ButtonVariant.Outlined} onClick={onDismiss}>
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} disabled={invalidForm}>
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  )
}
